import { BookOpen } from 'lucide-react'
import { defineModule } from '@/shell/module-system'
import { HelpIndexPage } from './pages/HelpIndexPage'
import { HelpDetailPage } from './pages/HelpDetailPage'

/**
 * Help portal — operator-facing documentation rendered from the
 * markdown tree at /opt/ai-coworker/docs/middleware/.
 *
 * First-class destination, NOT a popup. Sits in the Home sidebar
 * section so any operator can reach it from any context. Topbar
 * carries an always-visible Help button alongside the user menu;
 * each documented module's page header carries a `?` icon that
 * deep-links to /help/modules/<id>.
 *
 * Open to every authenticated role including viewer — read-only
 * reference material is precisely what viewers need (they can't
 * ask CARLA in chat). Source content + delivery surface decoupled
 * (Sprint shipped 2026-05-10): markdown lives in docs/, this
 * module just renders it.
 */
export default defineModule({
  id: 'help',
  name: 'Help',
  shortName: 'Help',
  icon: BookOpen,
  navSection: 'home',
  navOrder: 99,  // pinned to bottom of Home (after dashboard + executive)
  urlNamespace: '/help',
  routes: [
    { path: '/', element: HelpIndexPage, label: 'Help' },
    // Catch-all for /help/<section>/<slug>. Anchor scroll handled
    // client-side from the URL fragment (#section-id).
    { path: '/$section/$slug', element: HelpDetailPage, label: 'Help page' },
    { path: '/$section/$slug/$subSlug', element: HelpDetailPage, label: 'Help sub-page' },
  ],
  // Every chat-capable role + viewer (who specifically benefits from
  // reference docs since they can't ask CARLA).
  // outside_sales_rep excluded (2026-07-21): password-login field reps get
  // a narrowed sidebar (Orders + Quotes only) — no Help.
  permissions: [
    'admin', 'sales', 'inside_sales_rep',
    'operations', 'operations_logistics', 'purchasing', 'regulatory', 'finance', 'viewer',
  ],
  densityDefault: 'comfortable',
  filterRailDefault: 'collapsed',
  mobileStrategy: 'responsive',
  // 'g h' is taken by Dashboard (home); 'g ?' is the help convention
  // and the closest mnemonic for "go help".
  keyboardShortcut: 'g ?',
  commandPaletteEntries: [
    {
      label: 'Open Help portal',
      keywords: ['help', 'docs', 'documentation', 'reference', 'manual', 'how to', 'faq'],
      path: '/',
      group: 'modules',
      shortcut: 'g ?',
    },
  ],
})
